import { useMemo, useState } from 'react';
import { funnelApi, type Deal, type Pipeline, type Stage } from '../../services/funnelApi';
import { useAppToast } from '../../hooks/useAppToast';
import { StageColumn } from './StageColumn';

type Props = {
  pipelines: Pipeline[];
  pipelineId: string;
  deals: Deal[];
  selectedDealId: string | null;
  onSelectDeal: (dealId: string | null) => void;
  onDealsChanged: (deals: Deal[]) => void;
  loading?: boolean;
};

const HINT_ID = 'funil-move-hint';
const NO_STAGES: Stage[] = [];

/** Devolve o foco ao card depois que ele muda de coluna (o React o remonta). */
function focusDealCard(dealId: string) {
  requestAnimationFrame(() => {
    const btn = document.querySelector<HTMLElement>(`[data-deal-id="${CSS.escape(dealId)}"] .funil-card-open`);
    btn?.focus();
  });
}

/**
 * Board com colunas horizontais. Drag-and-drop via HTML5 nativo (sem lib):
 * `DealCard` coloca `text/deal-id` no dataTransfer, `StageColumn` lê no drop e
 * chama `onDropDeal`, que aqui otimisticamente move o card e chama a API.
 *
 * Alternativa por teclado (#229): o menu "…" de cada card lista "Mover para
 * <estágio>" e chama o mesmo `handleDropDeal`. O resultado é anunciado numa
 * região `aria-live` e o foco volta para o card na coluna nova.
 */
export function KanbanBoard({
  pipelines,
  pipelineId,
  deals,
  selectedDealId,
  onSelectDeal,
  onDealsChanged,
  loading,
}: Props) {
  const [dragOverStageId, setDragOverStageId] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const toast = useAppToast();

  const pipeline = useMemo(
    () => pipelines.find((p) => p.id === pipelineId) ?? null,
    [pipelines, pipelineId],
  );

  const stages: Stage[] = pipeline?.stages ?? NO_STAGES;

  const dealsByStage = useMemo(() => {
    const map = new Map<string, Deal[]>();
    for (const s of stages) map.set(s.id, []);
    for (const d of deals) {
      const list = map.get(d.stageId);
      if (list) list.push(d);
    }
    return map;
  }, [stages, deals]);

  const handleDropDeal = async (dealId: string, toStageId: string, { refocus = false } = {}) => {
    const deal = deals.find((d) => d.id === dealId);
    if (!deal) return;
    if (deal.stageId === toStageId) return;
    const toStageName = stages.find((s) => s.id === toStageId)?.name ?? 'outro estágio';

    // otimista: mover local primeiro
    const previousStageId = deal.stageId;
    const optimistic = deals.map((d) => (d.id === dealId ? { ...d, stageId: toStageId } : d));
    onDealsChanged(optimistic);
    setAnnouncement(`“${deal.title}” movido para ${toStageName}.`);
    if (refocus) focusDealCard(dealId);

    try {
      const res = await funnelApi.moveDeal(dealId, toStageId);
      const replaced = optimistic.map((d) => (d.id === dealId ? { ...d, ...res.deal } : d));
      onDealsChanged(replaced);
    } catch (e: unknown) {
      // rollback
      const rolled = optimistic.map((d) => (d.id === dealId ? { ...d, stageId: previousStageId } : d));
      onDealsChanged(rolled);
      setAnnouncement(`Não foi possível mover “${deal.title}”.`);
      if (refocus) focusDealCard(dealId);
      toast.error(e instanceof Error && e.message ? e.message : 'Falha ao mover card');
    }
  };

  if (!pipeline) {
    return <div className="text-kumo-subtle funil-empty">Pipeline não encontrado.</div>;
  }

  return (
    <>
      <span className="text-xs text-kumo-subtle sr-only" id={HINT_ID}>
        Para mudar o negócio de estágio sem arrastar, use o botão de mais ações do card e escolha “Mover para”.
      </span>
      <div className="sr-only" role="status" aria-live="polite">{announcement}</div>
      <div
        className="funil-board"
        data-testid="funil-board"
        role="region"
        aria-label="Quadro do funil (rola na horizontal)"
        tabIndex={0}
      >
        <h2 className="sr-only">Estágios do funil</h2>
        {stages.map((stage) => (
          <StageColumn
            key={stage.id}
            stage={stage}
            stages={stages}
            deals={dealsByStage.get(stage.id) ?? []}
            selectedDealId={selectedDealId}
            onSelectDeal={onSelectDeal}
            onDropDeal={handleDropDeal}
            onMoveDeal={(dealId, toStageId) => handleDropDeal(dealId, toStageId, { refocus: true })}
            isDragOverStageId={dragOverStageId}
            setDragOverStageId={setDragOverStageId}
            loading={loading}
            hintId={HINT_ID}
          />
        ))}
      </div>
    </>
  );
}
